<template>
    <div>
        <button class="sticky-contact-btn" type="button" :aria-expanded="isOpen ? 'true' : 'false'"
            aria-haspopup="dialog" @click="open">
            <span class="btn-text">{{ formT.open }}</span>
            <svg class="btn-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
        </button>

        <div v-if="isOpen" class="sticky-contact-overlay" @click.self="close">
            <div ref="dialogEl" class="sticky-contact-dialog" role="dialog" aria-modal="true" :aria-label="formT.title"
                tabindex="-1" @keydown="onDialogKeydown">
                <div class="sticky-contact-header">
                    <div class="sticky-contact-title">{{ formT.title }}</div>
                    <button class="sticky-contact-close" type="button" :aria-label="formT.close" @click="close">
                        ×
                    </button>
                </div>

                <form class="sticky-contact-form" @submit.prevent="submit">

                    <br>
                    <div v-if="!success">
                        <p class="sticky-contact-text">
                            {{ formT.desc_1 }}
                            <br>
                            {{ formT.desc_2 }}
                        </p>
                        <label class="sticky-contact-field">
                            <div class="sticky-contact-field custom-select-container">
                                <span class="sticky-contact-label">{{ formT.services_label }}</span>
                                <div class="custom-select-trigger" @click="toggleDropdown">
                                    <span>{{ formT.services[form.service] || formT.services_label }}</span>
                                    <span class="custom-select-arrow" :class="{ 'is-open': isDropdownOpen }">▼</span>
                                </div>
                                <ul v-if="isDropdownOpen" class="custom-select-options">
                                    <li v-for="(label, key) in formT.services" :key="key" @click="selectService(key)">
                                        {{ label }}
                                    </li>
                                </ul>
                            </div>
                        </label>
                        <label class="sticky-contact-field">
                            <span class="sr-only">{{ formT.name }}</span>
                            <input ref="firstFieldEl" v-model.trim="form.name" type="text" autocomplete="name" required
                                :placeholder="formT.name" />
                        </label>

                        <label class="sticky-contact-field">
                            <span class="sr-only">{{ formT.your_address }}</span>
                            <input ref="firstFieldEl" v-model.trim="form.email" type="email" autocomplete="email"
                                required :placeholder="formT.your_address" />
                        </label>
                        <!-- number phone optional -->
                        <label class="sticky-contact-field">
                            <span class="sr-only">{{ formT.number_phone }}</span>
                            <input ref="firstFieldEl" v-model.trim="form.number_phone" type="tel" autocomplete="tel"
                                required :placeholder="formT.number_phone" />
                        </label>

                        <!-- place ( city )-->
                        <label class="sticky-contact-field">
                            <span class="sr-only">{{ formT.city }}</span>
                            <input v-model.trim="form.city" type="text" required :placeholder="formT.city" />
                        </label>

                        <label class="sticky-contact-field">
                            <span class="sticky-contact-label">{{ formT.date }}</span>
                            <Datepicker v-model="form.date" :lowerLimit="new Date()" :inputFormat="'dd/MM/yyyy'"
                                :placeholder="'DD/MM/YYYY'" />
                        </label>

                        <label class="sticky-contact-field">
                            <span class="sr-only">{{ formT.subject }}</span>
                            <input v-model.trim="form.subject" type="text" maxlength="120" required
                                :placeholder="formT.subject" />
                        </label>

                        <label class="sticky-contact-field">
                            <span class="sr-only">{{ formT.message }}</span>
                            <textarea v-model.trim="form.message" rows="5" maxlength="2000" required
                                :placeholder="formT.message" />
                        </label>

                        <p v-if="errorMsg" class="sticky-contact-error" role="alert">
                            {{ errorMsg }}
                        </p>
                    </div>
                    <p v-if="success" class="sticky-contact-success" role="status">
                        {{ formT.success }}
                    </p>

                    <button v-if="!success" class="sticky-contact-submit" type="submit" :disabled="sending">
                        {{ sending ? formT.sending : (formT.button || formT.send) }}
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { computed, nextTick } from 'vue';
import translationsData from '@/assets/text_lang/translations.json';
import { useLanguageStore } from '@/theme';

import Datepicker from 'vue3-datepicker';


export default {
    name: 'StickyContactWidget',
    components: {
        Datepicker,
    },
    props: {
        endpointUrl: {
            type: String,
            default: process.env.VUE_APP_API_URL + '/email',
        },
    },

    data() {
        return {
            isDropdownOpen: false,
            isOpen: false,
            sending: false,
            success: false,
            errorMsg: '',
            lastFocusedEl: null,
            date: null,
            form: {
                name: '',
                email: '',
                number_phone: '',
                city: '',
                date: null,
                subject: '',
                message: '',
                service: 'obsluga eventu',
            },
        };
    },

    setup() {
        const languageStore = useLanguageStore();

        // const formT = computed(() => {
        //     const lang = languageStore.currentLanguage || 'pl';
        //     return translationsData?.[lang]?.contact_page?.form || {};
        // });
        const formT = computed(() => {
            const lang = languageStore.currentLanguage || 'pl';
            const contactPage = translationsData?.[lang]?.contact_page || {};
            return {
                ...contactPage.form,     // Rozpakowuje stare pola (name, email itp.)
                services: contactPage.services || {} // Dodaje usługi do obiektu
            };
        });

        const labels = computed(() => {
            const isEn = (languageStore.currentLanguage || 'pl') === 'en';
            return isEn
                ? {
                    open: 'contact us',
                    title: 'contact us',
                    close: 'close',
                    send: 'send',
                    sending: 'sending…',
                    success: 'message sent!',
                }
                : {
                    open: 'napisz do nas',
                    title: 'napisz do nas',
                    close: 'zamknij',
                    send: 'wyślij',
                    sending: 'wysyłanie…',
                    success: 'wiadomość wysłana!',
                };
        });

        return { formT, labels };
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideClick);
    },
    methods: {
        async open() {
            this.lastFocusedEl = document.activeElement;
            this.errorMsg = '';
            this.success = false;
            this.isOpen = true;

            await nextTick();
            const first = this.$refs.firstFieldEl;
            if (first && typeof first.focus === 'function') first.focus();
        },

        close() {
            this.isOpen = false;

            const el = this.lastFocusedEl;
            this.lastFocusedEl = null;
            if (el && typeof el.focus === 'function') el.focus();
        },

        onDialogKeydown(e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                this.close();
                return;
            }
            if (e.key !== 'Tab') return;

            const dialog = this.$refs.dialogEl;
            if (!dialog) return;

            const focusables = dialog.querySelectorAll(
                'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
            );
            if (!focusables.length) return;

            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const isShift = e.shiftKey === true;

            if (isShift && document.activeElement === first) {
                e.preventDefault();
                last.focus();
                return;
            }
            if (!isShift && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        },
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
        },
        selectService(value) {
            this.form.service = value;
            this.isDropdownOpen = false;
        },
        handleOutsideClick(e) {
            // Komputer sprawdza, czy kliknięto wewnątrz kontenera listy
            const container = this.$el.querySelector('.custom-select-container');
            if (container && !container.contains(e.target)) {
                this.isDropdownOpen = false;
            }
        },

        async submit() {
            this.errorMsg = '';
            this.success = false;

            const payload = {
                "email": this.form.email,
                "templateID": 7712173,
                "imie_nazwisko": this.form.name,
                "data_transparent": {
                    "addressee": this.form.email,
                    "type_case": this.formT.services[this.form.service] || this.formT.services_label,
                    "name": this.form.name,
                    "number_phone": this.form.number_phone,
                    "city": this.form.city,
                    "date": this.form.date.toLocaleDateString(), // tutaj tylko data bez godziny
                    "subject": this.form.subject,
                    "message": this.form.message,
                }
            };

            this.sending = true;
            try {
                await axios.post(this.endpointUrl, payload, { timeout: 10000 });
                this.success = true;
                this.form = { email: '', subject: '', message: '' };
            } catch (err) {
                this.errorMsg = 'Nie udało się wysłać wiadomości. Proszę spróbować ponownie.';
                console.error('Error sending email:', err);
            } finally {
                this.sending = false;
            }
        },
    },
};
</script>

<style scoped>
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.sticky-contact-btn {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 3000;
    width: 300px;
    height: 48px;
    padding: 0 16px;

    letter-spacing: 2px;
    text-transform: uppercase;

    border: 2px solid var(--dark-color-font);
    background: var(--dark-color-background);
    color: var(--dark-color-font);

    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
}

.sticky-contact-btn .btn-icon {
    display: none;
}

.sticky-contact-btn:hover {
    /* background: var(--dark-color-font); */
    background-color: rgba(255, 255, 255, 0.621);
    color: var(--dark-color-background);
    /* dajmuy grubsza czcionke w senise tekstu o jeden pixel */
    font-weight: bold;
    border: 2px solid var(--dark-color-background);

}
.sticky-contact-overlay {
    position: fixed;
    inset: 0;
    z-index: 4000;
    background: rgba(0, 0, 0, 0.5);
}

.sticky-contact-dialog {
    position: fixed;
    right: 20px;
    bottom: 80px;
    width: min(420px, calc(100vw - 40px));
    max-height: 70vh;
    /* overflow: hidden; */
    overflow-y: auto;
    display: flex;
    flex-direction: column;

    background: var(--dark-color-font);
    color: var(--dark-color-background);
    border: 2px solid var(--dark-color-background);
}

.sticky-contact-header {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;

    padding: 14px 14px 10px 14px;
    border-bottom: 1px solid var(--dark-color-background);
    background-color: var(--dark-color-background);
    color: var(--dark-color-font);

    position: sticky;
    top: 0;
    z-index: 2;
}

.sticky-contact-text {
    text-align: left;
    font-size: 12px;
    letter-spacing: 2px;
    text-transform: uppercase;
    font-weight: normal;
}

.sticky-contact-header .sticky-contact-title {
    grid-column: 2;
    justify-self: center;
    text-align: center;

    letter-spacing: 2px;
    text-transform: uppercase;
    font-size: 14px;
}

.sticky-contact-close {
    grid-column: 3;
    justify-self: end;
    color: var(--dark-color-font);
    background-color: var(--dark-color-background);
    font-size: 22px;
    border: 0 solid transparent;
}

.sticky-contact-close:hover {
    color: var(--dark-color-background);
    border: 0 solid transparent;
    background-color: var(--dark-color-font);
}


.sticky-contact-form {
    padding: 14px;
}

.sticky-contact-field input,
.sticky-contact-field textarea,
.sticky-contact-field select {
    width: 100%;
    margin: 10px 0;
    box-sizing: border-box;

    letter-spacing: 2px;
    text-transform: uppercase;

    border: 2px solid var(--dark-color-background);
    background-color: var(--dark-color-font);
    color: var(--dark-color-background);
    padding: 10px;
}


.sticky-contact-field select {
    width: 100%;
    margin: 10px 0;
    box-sizing: border-box;
    font-family: inherit;
    /* Panie, to zapewni taką samą czcionkę */
    letter-spacing: 2px;
    text-transform: uppercase;
    border: 2px solid var(--dark-color-background);
    background-color: var(--dark-color-font);
    color: var(--dark-color-background);
    padding: 10px;
    height: 48px;
    cursor: pointer;

    /* Ukrycie systemowego wyglądu */
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    /* Dodanie customowej strzałki */
    background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 15px center;
    background-size: 14px;
}

/* Opcje — działa w Chrome/Firefox; Safari/macOS może ignorować */
.sticky-contact-field select option {
    background-color: var(--dark-color-font);
    color: var(--dark-color-background);
}

.sticky-contact-submit {
    width: 100%;
    height: 48px;

    letter-spacing: 2px;
    text-transform: uppercase;

    border: 2px solid var(--dark-color-background);
    background-color: var(--dark-color-background);
    color: var(--dark-color-font);

    cursor: pointer;
}

.sticky-contact-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.sticky-contact-error {
    margin-top: 8px;
    color: #ffb3b3;
    letter-spacing: 1px;
}

.sticky-contact-success {
    margin-top: 8px;
    color: var(--dark-color-red);
    letter-spacing: 1px;
}

.sticky-contact-label {
    display: block;
    margin-top: 8px;
    margin-bottom: 6px;
    letter-spacing: 2px;
    text-transform: uppercase;
    font-size: 12px;
}

/* dropdown */
.custom-select-container {
    position: relative;
    cursor: pointer;
}

.custom-select-trigger {
    width: 100%;
    margin: 10px 0;
    box-sizing: border-box;
    /* font-weight: normal;  */
    border: 2px solid var(--dark-color-background);
    background-color: var(--dark-color-font);
    color: var(--dark-color-background);
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-transform: uppercase;
    letter-spacing: 2px;
    padding: 10px;
    font-size: 13px;
}

/* To jest ta lista, o którą Pan walczył */
.custom-select-options {
    position: absolute;
    /* margin-top: 10px; */
    top: 100%;
    left: 0;
    right: 0;
    z-index: 100;
    margin: 0px 0 0 0;
    /* Przyklejenie do pola */
    padding: 0;
    list-style: none;
    background-color: var(--dark-color-font);
    border: 2px solid var(--dark-color-background);
    font-size: 13px;
    /* Ostre kanty */
}

.custom-select-options li {
    padding: 12px 10px;
    text-transform: uppercase;
    letter-spacing: 2px;
    font-size: 14px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.custom-select-options li:last-child {
    border-bottom: none;
}

.custom-select-options li:hover {
    background-color: var(--dark-color-background);
    color: var(--dark-color-font);
}

.custom-select-arrow {
    transition: transform 0.3s ease;
    font-size: 10px;
}

.custom-select-arrow.is-open {
    transform: rotate(180deg);
}




/*  datepicker */
::v-deep .v3dp__popout {
    left: 2px;
    width: 100% !important;
    max-width: none !important;
}

::v-deep .v3dp__input_wrapper {
    width: 100%;
    font-size: 10px;
}

::v-deep .v3dp__input_wrapper input {
    width: 100%;
    font-family: 'MyFont', sans-serif;
    text-align: left;
    padding: 10px;
    border: 2px solid var(--dark-color-background);
    border-radius: 2px;
    background-color: var(--dark-color-font);
    color: var(--dark-color-background);
    letter-spacing: 2px;
    text-transform: uppercase;
    box-sizing: border-box;
}

::v-deep .v3dp__popout button,
::v-deep .v3dp__popout div,
::v-deep .v3dp__popout span {
    font-size: 10px;
}







@media (max-width: 700px) {
    .sticky-contact-dialog {
        right: 0;
        left: 0;
        bottom: 0;
        top: 0;
        width: 100vw;
        height: 100vh;
        max-height: 100vh;
        overflow-y: auto;
    }

    .sticky-contact-btn {
        right: 15px;
        bottom: 15%;
        width: 50px;
        height: 50px;
        /* border-radius: 50%; */
        padding: 0;
    }

    .sticky-contact-btn .btn-text {
        display: none;
    }

    .sticky-contact-btn .btn-icon {
        display: block;
        width: 24px;
        height: 24px;
    }

    .sticky-contact-close {
        font-size: 30px;
    }


    ::v-deep .v3dp__input_wrapper {
        font-size: 20px;
    }

    ::v-deep .v3dp__popout button,
    ::v-deep .v3dp__popout div,
    ::v-deep .v3dp__popout span {
        font-size: 16px;
    }
}
</style>